import { useParams, useSearchParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import ContentBlockRenderer from "@/components/content/ContentBlockRenderer";
import { useContentItem } from "@/hooks/useContentItem";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { staggerContainer, heroVariant, sectionVariant, viewportOnce } from "@/lib/motion";
import NotFound from "@/pages/NotFound";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const previewMode = searchParams.get("preview") === "true" && !!user;

  const { data: item, isLoading } = useContentItem(slug, previewMode);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-36 pb-24 container-wide max-w-3xl">
          <Skeleton className="h-6 w-32 mb-4" />
          <Skeleton className="h-12 w-full mb-6" />
          <Skeleton className="h-6 w-2/3 mb-8" />
          <Skeleton className="h-64 w-full" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!item || item.status === "archived") return <NotFound />;
  if ((item.status === "draft" || item.status === "review") && !previewMode) return <NotFound />;

  const content = item.content || {};
  const seo = item.seo || {};
  const blocks = content.blocks || [];
  const heroImage = content.hero_image || null;
  const ogImage = seo.ogImage || content.og_image?.src || null;

  const publishedDate = item.published_at
    ? new Date(item.published_at).toLocaleDateString("en-US", {
        year: "numeric", month: "long", day: "numeric",
      })
    : null;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: seo.title || item.title,
    description: seo.description || item.excerpt || "",
    datePublished: item.published_at,
    dateModified: item.updated_at,
    ...(ogImage && { image: ogImage }),
    author: { "@type": "Organization", name: "XperfoMax" },
    publisher: { "@type": "Organization", name: "XperfoMax", url: "https://xperfomax.com" },
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seo.title || item.title}
        description={seo.description || item.excerpt || ""}
        canonicalUrl={seo.canonicalUrl || `/blog/${item.slug}`}
        ogImage={ogImage}
        schema={articleSchema}
      />
      <Header />

      {previewMode && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/30 text-center py-2">
          <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
            Preview Mode. This content is not published.
          </span>
        </div>
      )}

      {/* Hero */}
      <section className="pt-28 pb-10 md:pt-36 md:pb-16 lg:pt-44 lg:pb-20 border-b border-border">
        <div className="container-wide">
          <div className="max-w-3xl">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.div variants={heroVariant}>
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Blog
                </Link>
              </motion.div>

              {content.category && (
                <motion.span variants={heroVariant} className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4 block">
                  {content.category}
                </motion.span>
              )}

              <motion.h1 variants={heroVariant} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-6 leading-[1.15]">
                {item.title}
              </motion.h1>

              {item.excerpt && (
                <motion.p variants={heroVariant} className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                  {item.excerpt}
                </motion.p>
              )}

              <motion.div variants={heroVariant} className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 text-sm text-muted-foreground">
                {publishedDate && <span>{publishedDate}</span>}
                {content.readTime && <span>{content.readTime} read</span>}
                {content.author && <span>By {content.author}</span>}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      {heroImage?.src && (
        <section className="border-b border-border">
          <div className="container-wide py-8 md:py-10">
            <figure className="max-w-3xl">
              <img
                src={heroImage.src}
                alt={heroImage.alt || item.title}
                width={heroImage.width || 1200}
                height={heroImage.height || 630}
                loading="eager"
                className="w-full h-auto max-h-[420px] object-cover border border-border"
              />
            </figure>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-16 md:py-20 lg:py-24">
        <div className="container-wide">
          <div className="max-w-3xl">
            <motion.article initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
              <ContentBlockRenderer blocks={blocks} />
            </motion.article>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-border">
        <div className="container-wide text-center">
          <p className="text-muted-foreground mb-6">Want to discuss this topic further?</p>
          <Button variant="primary" size="lg" asChild>
            <Link to="/contact">Start a conversation</Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPost;
